import { useCallback, useEffect, useRef, MutableRefObject } from "react";
import { useBrowserDetection } from "./useBrowserDetection";
import useChatStore from "../stores/chatStore";

export const useScrollHandling = ({
  messagesContainerRef,
  messageRefs,
  isScrollHandlerAttached,
  lastScrollTopRef,
  loadingOlder,
  targetMessageId,
  manualOverride,
  canPaginateUp,
  canPaginateDown,
  waitForAnimation,
  skipRefetchRef,
  setShowScrollButton,
  setIsAtBottom,
  setShowProgressBar,
  setTargetMessageId,
  loadOlderMessages,
  loadNewerMessages,
  isSmallScreen,
  scrollThresholdUp,
  scrollThresholdDown,
  MIN_SCROLL_TOP = 20,
  isSearchingMessage,
  scrollTimeoutRef,
  messageInViewIdRef,
}: {
  messagesContainerRef: MutableRefObject<HTMLDivElement | null>;
  messageRefs: React.RefObject<Record<string, HTMLDivElement | null>>;
  isScrollHandlerAttached: MutableRefObject<boolean>;
  lastScrollTopRef: MutableRefObject<number>;
  loadingOlder: boolean;
  targetMessageId: string | null;
  manualOverride: boolean;
  canPaginateUp: boolean;
  canPaginateDown: boolean;
  waitForAnimation: boolean;
  skipRefetchRef: MutableRefObject<boolean>;
  setShowScrollButton: (value: boolean) => void;
  setIsAtBottom: (value: boolean) => void;
  setShowProgressBar: (value: boolean) => void;
  setTargetMessageId: (value: null) => void;
  loadOlderMessages: () => Promise<any>;
  loadNewerMessages: () => Promise<any>;
  isSmallScreen: boolean;
  scrollThresholdUp: number;
  scrollThresholdDown: number;
  MIN_SCROLL_TOP?: number;
  isSearchingMessage: boolean;
  scrollTimeoutRef: React.RefObject<NodeJS.Timeout | null>;
  messageInViewIdRef: React.RefObject<string | null>;
}) => {
  const isLoadingRef = useRef(false);
  const tickingRef = useRef(false);
  const prevFlagsRef = useRef({
    showBtn: false,
    atBottom: true,
    showBar: false,
  });
  const userInteractingRef = useRef(false);
  const userInteractTORef = useRef<number | null>(null);
  const isUserScrollingDownRef = useRef(false);
  const bottomThresholdPx = 50;
  const userReachedBottomManuallyRef = useRef(false);
  const { isIOS } = useBrowserDetection();

  const handleLoadOlderMessages = useCallback(async () => {
    if (useChatStore.getState().isBootstrapping) return;
    if (loadingOlder || isLoadingRef.current) return;

    isLoadingRef.current = true;
    try {
      const container = messagesContainerRef.current;
      if (!container) return;

      const prevScrollHeight = container.scrollHeight;
      const prevScrollTop = container.scrollTop;

      await loadOlderMessages();
      await new Promise(requestAnimationFrame);
      const newScrollHeight = container.scrollHeight;
      const delta = newScrollHeight - prevScrollHeight;

      container.scrollTop = prevScrollTop + delta;

      if (isSmallScreen && container.scrollTop < MIN_SCROLL_TOP) {
        container.scrollTop = MIN_SCROLL_TOP;
      }
    } finally {
      isLoadingRef.current = false;
    }
  }, [
    loadingOlder,
    messagesContainerRef,
    loadOlderMessages,
    isSmallScreen,
    MIN_SCROLL_TOP,
  ]);

  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    if (tickingRef.current) return;

    tickingRef.current = true;
    requestAnimationFrame(() => {
      if (userInteractTORef.current)
        window.clearTimeout(userInteractTORef.current!);
      userInteractingRef.current = true;
      userInteractTORef.current = window.setTimeout(() => {
        userInteractingRef.current = false;
        setTimeout(() => {
          userReachedBottomManuallyRef.current = false;
        }, 100);
      }, 300);

      const scrollTop = container.scrollTop;
      const clientHeight = container.clientHeight;
      const scrollHeight = container.scrollHeight;
      const scrollBottom = scrollHeight - (scrollTop + clientHeight);

      isUserScrollingDownRef.current = scrollTop > lastScrollTopRef.current;

      if (
        isUserScrollingDownRef.current &&
        scrollBottom < bottomThresholdPx &&
        userInteractingRef.current
      ) {
        userReachedBottomManuallyRef.current = true;
      } else if (scrollTop < lastScrollTopRef.current) {
        userReachedBottomManuallyRef.current = false;
      }

      const isAtBottomIOS =
        isIOS &&
        (scrollTop + clientHeight >= scrollHeight - 10 ||
          scrollBottom <= 10);

      const nextShowBtn = isIOS
        ? !isAtBottomIOS
        : scrollBottom > 200;
      const nextAtBottom = isIOS
        ? isAtBottomIOS
        : scrollBottom < bottomThresholdPx;
      const nextShowBar =
        !manualOverride &&
        (targetMessageId ? false : scrollTop >= lastScrollTopRef.current);

      if (prevFlagsRef.current.showBtn !== nextShowBtn) {
        prevFlagsRef.current.showBtn = nextShowBtn;
        setShowScrollButton(nextShowBtn);
      }
      if (prevFlagsRef.current.atBottom !== nextAtBottom) {
        prevFlagsRef.current.atBottom = nextAtBottom;
        setIsAtBottom(nextAtBottom);
      }
      if (prevFlagsRef.current.showBar !== nextShowBar) {
        prevFlagsRef.current.showBar = nextShowBar;
        setShowProgressBar(nextShowBar);
      }

      lastScrollTopRef.current = scrollTop;

      const ratio = scrollTop / Math.max(1, scrollHeight - clientHeight);

      if (
        ratio < scrollThresholdUp &&
        !loadingOlder &&
        canPaginateUp &&
        !isLoadingRef.current &&
        !useChatStore.getState().isBootstrapping
      ) {
        handleLoadOlderMessages();
      } else if (
        ratio > scrollThresholdDown &&
        (targetMessageId || canPaginateDown) &&
        !skipRefetchRef.current &&
        !waitForAnimation &&
        !isLoadingRef.current &&
        !isSearchingMessage
      ) {
        loadNewerMessages().then((newMsgs) => {
          if (newMsgs !== null && newMsgs.length === 0 && !waitForAnimation) {
            setTargetMessageId(null);
            skipRefetchRef.current = true;
          }
        });
      }

      tickingRef.current = false;
    });
  }, [
    messagesContainerRef,
    manualOverride,
    targetMessageId,
    lastScrollTopRef,
    loadingOlder,
    canPaginateUp,
    canPaginateDown,
    waitForAnimation,
    skipRefetchRef,
    isSearchingMessage,
    scrollThresholdUp,
    scrollThresholdDown,
    handleLoadOlderMessages,
    loadNewerMessages,
    setShowScrollButton,
    setIsAtBottom,
    setShowProgressBar,
    setTargetMessageId,
    isIOS,
  ]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || isScrollHandlerAttached.current) return;

    isScrollHandlerAttached.current = true;
    container.addEventListener("scroll", handleScroll);

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
        isScrollHandlerAttached.current = false;
      }
      if (userInteractTORef.current) {
        clearTimeout(userInteractTORef.current);
      }
      userReachedBottomManuallyRef.current = false;
    };
  }, [messagesContainerRef, isScrollHandlerAttached, handleScroll]);

  const shouldAutoScrollForMessageType = useCallback((messageType: string) => {
    const autoScrollTypes = [
      "command",
      "default",
      "disable",
      "feedback",
      "info",
      "search_result_high",
      "search_result_low",
      "search_result_middle",
      "stars0",
      "stars5",
      "suggestion",
      "system",
      "play_gif_once",
    ];
    return autoScrollTypes.includes(messageType);
  }, []);

  const scrollToBottomWhenAtBottom = useCallback(
    (isAtBottom: boolean) => {
      if (isAtBottom && !targetMessageId && messagesContainerRef.current) {
        messagesContainerRef.current.scrollTo({
          top: messagesContainerRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    },
    [targetMessageId, messagesContainerRef]
  );

  const scrollToBottomForMessageType = useCallback(
    (messageType: string, isAtBottom: boolean = false) => {
      if (
        shouldAutoScrollForMessageType(messageType) &&
        !targetMessageId &&
        messagesContainerRef.current &&
        isAtBottom
      ) {
        messagesContainerRef.current.scrollTo({
          top: messagesContainerRef.current.scrollHeight,
          behavior: "auto",
        });
      }
    },
    [shouldAutoScrollForMessageType, targetMessageId, messagesContainerRef]
  );

  return {
    handleLoadOlderMessages,
    handleScroll,
    scrollToBottomWhenAtBottom,
    scrollToBottomForMessageType,
    shouldAutoScrollForMessageType,
  };
};
